import React from 'react';
import { TypographyContainer, TypographyComponentProps, TypographySize } from './Typography.styles';
import { componentPropsToStyledProps } from 'src/app/styles/helper';
import { useContentTranslation } from 'src/hooks/useContentTranslation';

export interface TypographyProps extends TypographyComponentProps {
  children?: React.ReactNode;
  className?: string;
  text?: string | React.ReactNode;
  dataCy?: string;
  id?: string;
  skipTranslation?: boolean; // Escape hatch for when translation isn't wanted
  as?: React.ElementType;
}

const HEADING_TAG_MAP: Record<TypographySize, 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'> = {
  '3xl': 'h1',
  '2xl': 'h1',
  xl: 'h2',
  lg: 'h3',
  md: 'h4',
  sm: 'h5',
  xs: 'h6',
};

const getHeadingTag = (size: TypographySize): 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' => {
  return HEADING_TAG_MAP[size];
};

export const Typography = ({
  children,
  text,
  className,
  type,
  size,
  dataCy,
  id,
  skipTranslation,
  as,
  ...props
}: TypographyProps): JSX.Element => {
  // Determine what content to translate - prioritize children over text
  const contentToTranslate = children || text;

  // Use translation hook
  const translatedContent = useContentTranslation(contentToTranslate, { skipTranslation });

  // Use translated content if available, otherwise fallback to original children/text
  const processedContent = translatedContent || children || text;

  const tag = type === 'heading' ? getHeadingTag(size) : 'p';

  return (
    <TypographyContainer
      className={className}
      as={as || tag}
      $type={type}
      $size={size}
      data-cy={dataCy}
      id={id}
      {...componentPropsToStyledProps(props)}
    >
      {processedContent}
    </TypographyContainer>
  );
};

export default Typography;
